/**
 * Inicialización y helpers de Mixpanel (analítica de producto de wcar.co).
 *
 * El token va hardcodeado igual que el resto de identificadores de terceros del
 * proyecto (GTM, GA4 y Clarity en `public/index.html`, el client id de Google en
 * `App.tsx`, la URL del backend en `HostApi.tsx`). Es público por diseño: viaja
 * en el bundle del navegador de todas formas, así que no gana nada escondido en
 * una variable de entorno — y el proyecto no usa ninguna.
 *
 * ⚠️ Este archivo es la ÚNICA capa que habla con el SDK. El resto del código usa
 * `src/lib/analytics.ts`, que expone un catálogo de eventos tipado. No importes
 * `mixpanel-browser` directamente en componentes.
 *
 * ⚠️ PRIVACIDAD — ESTA WEB ES DISTINTA A TECH-COMPRAS:
 * en wcar.co el usuario ES el cliente final, no un empleado. Sus datos (nombre,
 * correo, teléfono, cédula, placa) están cubiertos por la Ley 1581 de 2012
 * (habeas data), así que a Mixpanel NO va NINGÚN dato personal: ni en eventos ni
 * en el perfil. La identidad se resuelve con el id interno de la cuenta y nada
 * más. `limpiarProps()` descarta cualquier clave que parezca PII y, a diferencia
 * del proyecto interno, aquí NO hay excepciones para correo.
 *
 * SIN SESSION REPLAY: la entrada `core` del SDK no incluye el grabador. La web
 * ya tiene Microsoft Clarity y Contentsquare para grabación de sesión; Mixpanel
 * cubre lo que ellos no: eventos de producto, embudos y cohortes.
 *
 * Convive con GTM/GA4 (medios y tráfico) sin reemplazarlos: GA4 responde
 * "cuánta gente llegó y por dónde"; Mixpanel, "qué hicieron adentro y quiénes
 * volvieron".
 */
// Entrada `core`: el bundle por defecto de `mixpanel-browser` trae el grabador de
// sesión EMBEBIDO (1.1 MB sin minificar) aunque no se use. El core pesa 368 kB y
// no tiene forma de bajar el grabador del CDN. En un ecommerce con SEO, ese peso
// entra directo en Core Web Vitals, así que la entrada correcta es esta.
import mixpanel from "mixpanel-browser/dist/mixpanel-core.cjs.js";
import { MIXPANEL_TOKEN } from "../config/env";

/** Valores admitidos como propiedad de evento. `undefined` se descarta. */
export type PropValue = string | number | boolean | string[] | number[] | undefined;
export type EventProps = Record<string, PropValue>;

// Token del proyecto de Mixpanel (id 4065820).

/** Activo solo si `init()` corrió sin errores. */
let enabled = false;

const esProduccion = (): boolean => process.env.NODE_ENV === "production";

/**
 * Entorno lógico del evento, deducido de `NODE_ENV` (built-in de CRA: vale
 * `development` con `npm start` y `production` con `npm run build`). Va como
 * super propiedad en TODOS los eventos.
 *
 * Mientras exista un solo proyecto de Mixpanel, esta propiedad es lo único que
 * separa el ruido de desarrollo de las métricas reales — filtrar siempre por
 * `environment == "production"`. Es el mismo comportamiento que ya tienen GTM y
 * Clarity, que también disparan desde localhost.
 */
const environment = (): string => (esProduccion() ? "production" : "development");

/**
 * Claves prohibidas: datos personales del cliente que jamás deben salir del
 * navegador hacia Mixpanel. En desarrollo avisa por consola; siempre descarta.
 *
 * `placa` está en la lista porque el buscador del catálogo permite filtrar por
 * placa: sin este guardarraíl, el término de búsqueda podría arrastrar el dato
 * de un vehículo real.
 */
const CLAVES_PII = [
    "cedula",
    "documento",
    "telefono",
    "celular",
    "whatsapp_numero",
    "correo",
    "email",
    "mail",
    "nombre",
    "apellido",
    "placa",
    "plate",
    "direccion",
    "barrio",
    "tarjeta",
    "password",
    "contrasena",
];

/**
 * A diferencia del proyecto interno de compras, aquí NO hay excepciones: el
 * usuario de wcar.co es el cliente, así que ninguna clave con correo, nombre o
 * teléfono puede viajar, ni siquiera la del propio usuario autenticado.
 */
const esClavePii = (key: string): boolean => {
    const k = key.toLowerCase();
    return CLAVES_PII.some((prohibida) => k.includes(prohibida));
};

/**
 * Formato EXACTO de placa colombiana: ABC123 (carro) y ABC12D (moto), con o sin
 * un separador. Exigir el formato exacto —3 letras y 3 dígitos, ni más ni
 * menos— es lo que deja pasar "mazda 3", "hilux 4x4", "320i" o "kia sportage",
 * que son la señal de demanda que este evento existe para capturar.
 *
 * ⚠️ Decisión consciente: "bmw 320" tiene exactamente la misma forma que una
 * placa, así que también se descarta. Se prefiere perder ese puñado de búsquedas
 * de marca+modelo numérico antes que arriesgar mandar la placa de un vehículo
 * real, que es dato personal bajo la Ley 1581. Si el equipo decide que esas
 * búsquedas pesan más, quitar `[\s-]?` recupera las que llevan espacio — pero
 * es un cambio que hay que tomar a propósito, no relajar de pasada.
 */
const PATRON_PLACA = /^(?:[a-z]{3}[\s-]?\d{3}|[a-z]{3}[\s-]?\d{2}[a-z])$/i;

/**
 * Normaliza un término de búsqueda libre antes de mandarlo.
 *
 * El buscador del catálogo dice "marca, modelo, color...", pero también acepta
 * placas. El término de búsqueda es una de las métricas más valiosas del
 * ecommerce (dice qué demanda existe y qué inventario falta), así que no se
 * descarta: se normaliza a minúscula, se recorta a 60 caracteres para no
 * disparar la cardinalidad, y se DESCARTA por completo si parece una placa.
 */
export const limpiarTerminoBusqueda = (termino: string | undefined): string | undefined => {
    const t = (termino ?? "").trim().toLowerCase().replace(/\s+/g, " ");
    if (!t) return undefined;
    if (PATRON_PLACA.test(t)) return undefined;
    return t.slice(0, 60);
};

/**
 * Normaliza propiedades antes de enviarlas:
 * - descarta `undefined`, `null`, strings vacíos y arrays vacíos (Mixpanel
 *   prefiere la ausencia de la propiedad sobre un `null`/`""`, que ensucian los
 *   reportes);
 * - descarta claves con PII (ver `CLAVES_PII`);
 * - deja los números como números (mandarlos como string los vuelve no
 *   agregables en Mixpanel: no se puede sumar ni promediar).
 */
const limpiarProps = (props: EventProps | undefined): Record<string, any> => {
    const out: Record<string, any> = {};
    if (!props) return out;
    for (const [key, value] of Object.entries(props)) {
        if (value === undefined || value === null || value === "") continue;
        if (Array.isArray(value) && value.length === 0) continue;
        if (esClavePii(key)) {
            if (process.env.NODE_ENV !== "production") {
                console.warn(`[mixpanel] propiedad "${key}" descartada: parece dato personal del cliente.`);
            }
            continue;
        }
        if (typeof value === "number" && !Number.isFinite(value)) continue;
        out[key] = value;
    }
    return out;
};

/**
 * Atribución de PRIMER contacto, como super propiedades permanentes.
 *
 * Mixpanel adjunta los `utm_*` de la URL solo al evento en que aparecen. En un
 * ecommerce de usados el ciclo de compra dura semanas: la conversión casi nunca
 * pasa en la visita del anuncio. Con `register_once` la fuente original queda
 * pegada al dispositivo y viaja en TODOS los eventos futuros, así que la cita
 * agendada tres visitas después sigue siendo atribuible a la campaña que la
 * originó — que es la pregunta que paga la pauta.
 *
 * `register_once` no pisa un valor ya guardado: el primer contacto se conserva.
 */
const registrarAtribucionInicial = (): void => {
    const params = new URLSearchParams(window.location.search);
    const get = (key: string): string | undefined => params.get(key)?.trim().toLowerCase() || undefined;

    const utmSource = get("utm_source");
    const utmMedium = get("utm_medium");
    const gclid = params.get("gclid") ? "google_ads" : undefined;
    const fbclid = params.get("fbclid") ? "meta_ads" : undefined;

    // Canal en una sola propiedad: evita que cada análisis tenga que rearmar la
    // misma lógica de "¿esto fue pago, orgánico o directo?" a mano.
    let canal = "directo";
    if (gclid || fbclid || utmMedium === "cpc" || utmMedium === "ppc" || utmMedium === "paid") canal = "pago";
    else if (utmSource) canal = utmSource;
    else if (document.referrer) {
        try {
            const host = new URL(document.referrer).hostname.replace(/^www\./, "");
            if (host && host !== window.location.hostname.replace(/^www\./, "")) {
                canal = /google|bing|duckduckgo|yahoo/.test(host) ? "organico" : host;
            }
        } catch (_) {
            /* referrer inválido: se queda en "directo" */
        }
    }

    mixpanel.register_once(
        limpiarProps({
            primer_canal: canal,
            primer_utm_source: utmSource ?? gclid ?? fbclid,
            primer_utm_medium: utmMedium,
            primer_utm_campaign: get("utm_campaign"),
            primer_utm_content: get("utm_content"),
            primer_utm_term: get("utm_term"),
            // Landing de entrada SIN query string: la ruta dice qué contenido
            // capta tráfico; los parámetros solo inflarían la cardinalidad.
            primera_landing: window.location.pathname,
        }),
    );
};

/** Inicializa Mixpanel. Llamar una sola vez antes del primer render. */
export function initMixpanel(): void {
    // Sin token el SDK queda a medio inicializar y `register` lanza antes del primer render (pantalla en blanco).
    if (!MIXPANEL_TOKEN) return;
    try {
        mixpanel.init(MIXPANEL_TOKEN, {
            // Sin Session Replay. La entrada `core` ya no puede grabar, pero el 0
            // va explícito: es la línea que dice que fue una decisión, no un
            // descuido, y el cinturón si alguien cambia el import de vuelta.
            record_sessions_percent: 0,
            // `debug: true` loguea cada llamada en consola. Solo fuera de producción.
            debug: !esProduccion(),
            // Sin auto-pageview: es una SPA y el SDK solo dispararía en la carga
            // inicial. Las vistas se emiten a mano con `useTrackOnce`.
            track_pageview: false,
            // Más confiable que la cookie por defecto (no se pierde entre
            // subdominios ni con límites de tamaño de cookie).
            persistence: "localStorage",
        });
        enabled = true;
    } catch (err) {
        // Un fallo de analítica nunca debe tumbar la web.
        console.error("[mixpanel] init falló; la analítica queda desactivada.", err);
        return;
    }

    // Super propiedades de contexto: viajan en todos los eventos automáticamente.
    mixpanel.register({
        platform: "web",
        environment: environment(),
    });

    registrarAtribucionInicial();
}

/**
 * Envía un evento. Uso interno: los componentes llaman a `trackEvent()` de
 * `analytics.ts`, que valida el nombre y las propiedades contra el catálogo.
 *
 * `inmediato` salta la cola de envío por lotes del SDK. Hay que usarlo en los
 * eventos que ocurren JUSTO antes de abandonar la página (recarga tras el login
 * con Google, `window.open` a WhatsApp, redirección a la pasarela de pago): si
 * el evento se queda en el lote, el navegador descarga la página antes de que
 * salga y el evento se pierde — precisamente en los puntos de mayor valor.
 */
export function mpTrack(name: string, props?: EventProps, inmediato = false): void {
    if (!enabled) return;
    mixpanel.track(name, limpiarProps(props), inmediato ? { send_immediately: true } : undefined);
}

/**
 * Arranca el cronómetro de un evento. Al hacer `mpTrack(name)` más tarde,
 * Mixpanel adjunta `$duration` (segundos) automáticamente. Se usa para medir
 * cuánto tarda el cliente en completar un agendamiento o una cotización.
 */
export function mpStartTimer(name: string): void {
    if (!enabled) return;
    mixpanel.time_event(name);
}

/**
 * Asocia la sesión a la cuenta del cliente y enlaza toda su actividad anónima
 * previa (las visitas al catálogo antes de registrarse) con su perfil.
 *
 * ⚠️ Solo viaja el id interno. NADA de nombre, correo ni teléfono: ver la nota
 * de privacidad al inicio del archivo. El perfil guarda únicamente marcas de
 * tiempo, que permiten hacer cohortes por fecha de registro sin identificar a
 * nadie dentro de Mixpanel.
 */
export function identifyMixpanelUser(userId: string | number): void {
    if (!enabled) return;
    const id = String(userId ?? "").trim();
    if (!id) return;

    mixpanel.identify(id);
    mixpanel.people.set({ ultimo_acceso: new Date().toISOString() });
    mixpanel.people.set_once({ primer_acceso: new Date().toISOString() });
}

/**
 * Limpia la identidad al cerrar sesión: genera un `$device_id` nuevo.
 *
 * ⚠️ Solo en logout explícito. Si no se llama, los eventos del siguiente usuario
 * en el mismo equipo se mezclan con el perfil del anterior — corrupción de datos
 * y problema de privacidad. Y si se llamara de más (p. ej. en cada carga sin
 * sesión), se rompería el hilo de las visitas anónimas, que en este ecommerce
 * son la mayoría del tráfico.
 */
export function resetMixpanelUser(): void {
    if (!enabled) return;
    mixpanel.reset();
    // `reset()` borra también las super propiedades: las de contexto se vuelven a
    // registrar para que los eventos posteriores al logout no salgan sin ellas.
    mixpanel.register({
        platform: "web",
        environment: environment(),
    });
}
